import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Divider,
  Paper,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Percent as PercentIcon,
  Euro as EuroIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DiscountDialog = ({ open, onClose, currentTotal, onApplyDiscount }) => {
  const { t } = useTranslation();
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' or 'amount'
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');

  const handleDiscountTypeChange = (event, newType) => {
    if (newType !== null) {
      setDiscountType(newType);
      setDiscountValue('');
    }
  };

  const calculateDiscount = () => {
    const value = parseFloat(discountValue) || 0;
    if (discountType === 'percentage') {
      return (currentTotal * value) / 100;
    }
    return value;
  };

  const discountAmount = calculateDiscount();
  const newTotal = Math.max(0, currentTotal - discountAmount);

  const handleApply = () => {
    if (!discountValue || parseFloat(discountValue) <= 0) {
      return;
    }

    const discount = {
      type: discountType,
      value: parseFloat(discountValue),
      amount: discountAmount,
      reason: discountReason,
    };

    onApplyDiscount(discount);
    handleClose();
  };

  const handleClose = () => {
    setDiscountValue('');
    setDiscountReason('');
    setDiscountType('percentage');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PercentIcon />
          <Typography variant="h6" fontWeight={700}>
            {t('apply_discount') || 'Apply Discount'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Current Total Display */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: alpha('#667eea', 0.05),
            border: '2px solid',
            borderColor: alpha('#667eea', 0.2),
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t('current_total') || 'Current Total'}
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary">
            €{currentTotal.toFixed(2)}
          </Typography>
        </Paper>

        {/* Discount Type Selector */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('discount_type') || 'Discount Type'}
          </Typography>
          <ToggleButtonGroup
            fullWidth
            value={discountType}
            exclusive
            onChange={handleDiscountTypeChange}
            sx={{ mb: 2 }}
          >
            <ToggleButton
              value="percentage"
              sx={{
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: alpha('#ff9800', 0.2),
                  color: '#ff9800',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: alpha('#ff9800', 0.3),
                  },
                },
              }}
            >
              <PercentIcon sx={{ mr: 1 }} />
              {t('percentage') || 'Percentage'}
            </ToggleButton>
            <ToggleButton
              value="amount"
              sx={{
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: alpha('#ff9800', 0.2),
                  color: '#ff9800',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: alpha('#ff9800', 0.3),
                  },
                },
              }}
            >
              <EuroIcon sx={{ mr: 1 }} />
              {t('fixed_amount') || 'Fixed Amount'}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Discount Value Input */}
          <TextField
            fullWidth
            label={
              discountType === 'percentage'
                ? t('discount_percentage') || 'Discount Percentage'
                : t('discount_amount') || 'Discount Amount'
            }
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {discountType === 'percentage' ? <PercentIcon /> : <EuroIcon />}
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              max: discountType === 'percentage' ? 100 : currentTotal,
              step: discountType === 'percentage' ? 1 : 0.01,
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Quick Percentage Buttons */}
        {discountType === 'percentage' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom display="block">
              {t('quick_discounts') || 'Quick Discounts'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[5, 10, 15, 20, 25, 50].map((percent) => (
                <Button
                  key={percent}
                  variant="outlined"
                  size="small"
                  onClick={() => setDiscountValue(percent.toString())}
                  sx={{
                    minWidth: 60,
                    borderColor: alpha('#ff9800', 0.5),
                    color: '#ff9800',
                    '&:hover': {
                      borderColor: '#ff9800',
                      bgcolor: alpha('#ff9800', 0.1),
                    },
                  }}
                >
                  {percent}%
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* Discount Reason */}
        <TextField
          fullWidth
          label={t('discount_reason') || 'Discount Reason (Optional)'}
          value={discountReason}
          onChange={(e) => setDiscountReason(e.target.value)}
          multiline
          rows={2}
          placeholder={t('discount_reason_placeholder') || 'e.g., Loyalty discount, Promotion, etc.'}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Discount Summary */}
        {discountValue && parseFloat(discountValue) > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: alpha('#4caf50', 0.05),
              border: '2px solid',
              borderColor: alpha('#4caf50', 0.3),
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CalculateIcon color="success" />
              <Typography variant="subtitle2" fontWeight={700} color="success.main">
                {t('discount_summary') || 'Discount Summary'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('original_total') || 'Original Total'}:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                €{currentTotal.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('discount') || 'Discount'} (
                {discountType === 'percentage'
                  ? `${discountValue}%`
                  : `€${parseFloat(discountValue).toFixed(2)}`}
                ):
              </Typography>
              <Typography variant="body2" fontWeight={600} color="error.main">
                -€{discountAmount.toFixed(2)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {t('new_total') || 'New Total'}:
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                €{newTotal.toFixed(2)}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="success.main"
              sx={{ display: 'block', mt: 1, fontWeight: 600 }}
            >
              💰 {t('you_save') || 'You save'}: €{discountAmount.toFixed(2)}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!discountValue || parseFloat(discountValue) <= 0}
          sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            minWidth: 150,
            '&:hover': {
              background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
            },
          }}
        >
          {t('apply_discount') || 'Apply Discount'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountDialog;